import * as React from 'react'

import mapboxgl from 'mapbox-gl'
import Map, {
  AttributionControl,
  MapProps,
  MapRef,
  useMap,
  ViewState,
} from 'react-map-gl/mapbox'

import Config from '~/client/src/shared/Config'
import {
  MAX_PITCH_LEVEL,
  MAX_ZOOM_LEVEL,
} from '~/client/src/shared/map/mapZoomRule'

import { MAP_ATTRIBUTION_ICON } from './mapAttributionIcon'
import { rewriteGlyphUrl } from './mapGlyphRewrite'
import MapLayers from './MapLayers'
import { WHEEL_ZOOM_RATE, ZOOM_RATE } from './mapScrollZoomRule'

import './MapCanvas.css'

// Omitted from the props so no surface can diverge: every one that set these itself did.
// The gesture set is here for the same reason one step later (STX1-1061): it was hand-rolled
// identically twice and absent from four of the six mount sites, which ran mapbox's defaults.
type IProps = Omit<
  MapProps,
  | 'maxZoom'
  | 'maxPitch'
  | 'bearingSnap'
  | 'accessToken'
  | 'attributionControl'
  | 'logoPosition'
  | 'doubleClickZoom'
  | 'dragPan'
  | 'dragRotate'
  | 'scrollZoom'
  | 'boxZoom'
  | 'keyboard'
  | 'touchZoomRotate'
  | 'touchPitch'
> & {
  // For the consumers that read this from outside the map tree; inside, use MapStyleGate or the hook.
  onStyleLoadedChange?: (isLoaded: boolean) => void
  // Separate booleans, not the store's style object: the mount sites are observers, and the field
  // reads have to happen in their render. Undefined = the surface offers no such control.
  isTrafficEnabled?: boolean
  isTerrainEnabled?: boolean
  isThreeDEnabled?: boolean
  shouldHideLabels?: boolean
  // The one sanctioned reason to lower maxPitch. Clamps pitch, blocks tilting, leaves bearing.
  isPitchLocked?: boolean
  // Locks the camera and every map event without touching mapbox's handler objects — see the
  // .map-inert rule. Disabling the handlers instead is what broke markup dragging (STX1-1061).
  isInert?: boolean
}

// A fact about the map, so the container answers it — no consumer wires its own style.load listener.
const MapStyleLoadedContext = React.createContext(false)

export const useIsMapStyleLoaded = (): boolean =>
  React.useContext(MapStyleLoadedContext)

export const MapStyleGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (useIsMapStyleLoaded() ? <>{children}</> : null)

const StyleLoadedProvider: React.FC<{
  onChange?: (isLoaded: boolean) => void
  children: React.ReactNode
}> = ({ onChange, children }) => {
  const { current: mapRef } = useMap()
  const [isLoaded, setIsLoaded] = React.useState(false)

  // Layout, not passive: react-map-gl starts loading the style before a passive effect would run,
  // so style.load could land before the listener exists (STX1-1045).
  React.useLayoutEffect(() => {
    // Reset on entry: a new map reaches this, an unmounting one does not.
    setIsLoaded(false)
    const map = mapRef?.getMap?.()
    if (!map) {
      return
    }

    const set = (value: boolean) => {
      setIsLoaded(value)
      onChange?.(value)
    }
    // Not isStyleLoaded(): with the v3 basemap arriving as an import it reads false long after idle.
    if (map.loaded()) {
      set(true)
    }
    const onStyleLoad = () => set(true)
    const onStyleLoading = () => set(false)
    map.on('style.load', onStyleLoad)
    map.on('styledataloading', onStyleLoading)
    // style.load fires once, so a ref arriving after it needs idle to catch up: idle repeats, and
    // cannot fire mid-load.
    map.on('idle', onStyleLoad)

    return () => {
      map.off('style.load', onStyleLoad)
      map.off('styledataloading', onStyleLoading)
      map.off('idle', onStyleLoad)
      // Reported, not just unsubscribed: the mirror outlives this map, and a stale true leaves
      // MapViewsGallery's style lock inert while the next one loads.
      onChange?.(false)
    }
  }, [mapRef, onChange])

  return (
    <MapStyleLoadedContext.Provider value={isLoaded}>
      {children}
    </MapStyleLoadedContext.Provider>
  )
}

// Flip by hand to see GL's placement boxes; every map gets it, which is the point of it living
// here rather than on one store.
const DEBUG_COLLISION = false

const INERT_CLASS = 'map-inert'
const ROTATING_CLASS = 'map-rotating'

// These need the map object, not props: <Map> takes no className and the zoom rates are setters.
// Layout, not passive — a paint with the map still live is a frame an inert surface can be dragged in.
const MapInstanceRules: React.FC<{ isInert?: boolean }> = ({ isInert }) => {
  const { current: mapRef } = useMap()

  React.useLayoutEffect(() => {
    const map = mapRef?.getMap?.()
    if (!map) {
      return
    }
    map.scrollZoom.setZoomRate(ZOOM_RATE)
    map.scrollZoom.setWheelZoomRate(WHEEL_ZOOM_RATE)
    if (DEBUG_COLLISION) {
      map.showCollisionBoxes = true
    }
  }, [mapRef])

  React.useLayoutEffect(() => {
    const container = mapRef?.getMap?.()?.getContainer?.()
    if (!container) {
      return
    }
    container.classList.toggle(INERT_CLASS, !!isInert)

    return () => container.classList.remove(INERT_CLASS)
  }, [mapRef, isInert])

  // Mapbox's own grabbing hangs off :active, which browsers only enter for the primary button, so
  // the right-button pitch never gets it. Both starts: a horizontal drag reports only a bearing
  // delta, a vertical one only a pitch. Both ends, not moveend: a programmatic camera call mid-drag
  // stops the handlers with allowEndAnimation false, which skips moveend but still fires these.
  React.useLayoutEffect(() => {
    const map = mapRef?.getMap?.()
    if (!map) {
      return
    }
    const container = map.getContainer()
    // originalEvent is present for a user gesture and absent for a programmatic camera move,
    // which fires the same events and must not show a grab hand.
    const arm = (e: { type: string; originalEvent?: Event }): void => {
      if (e?.originalEvent) {
        container.classList.add(ROTATING_CLASS)
      }
    }
    const disarm = (): void => container.classList.remove(ROTATING_CLASS)

    map.on('rotatestart', arm)
    map.on('pitchstart', arm)
    map.on('rotateend', disarm)
    map.on('pitchend', disarm)

    return () => {
      map.off('rotatestart', arm)
      map.off('pitchstart', arm)
      map.off('rotateend', disarm)
      map.off('pitchend', disarm)
      disarm()
    }
  }, [mapRef])

  return null
}

type ICamera = Pick<
  ViewState,
  'longitude' | 'latitude' | 'zoom' | 'bearing' | 'pitch' | 'padding'
>

// Padding lives on the transform, so a surface that framed itself with fitBounds({padding})
// still holds it — and those fits guard against running twice, so dropping it here would
// shift the viewport for the rest of the session.
export const readCamera = (map: mapboxgl.Map): ICamera => {
  const center = map.getCenter()

  return {
    longitude: center.lng,
    latitude: center.lat,
    zoom: map.getZoom(),
    bearing: map.getBearing(),
    pitch: map.getPitch(),
    padding: map.getPadding(),
  }
}

// Per map, not per process: iOS reclaims every context at once, so a shared budget would let
// the first maps recover and strand the rest. A map that keeps losing its own context spends its
// own budget and stops churning.
const RECOVERY_LIMIT = 3
const RECOVERY_WINDOW_MS = 60_000

// performance.now, so a clock change cannot freeze the window open or shut.
export const withinBudget = (at: readonly number[], now: number): number[] =>
  at.filter(recordedAt => now - recordedAt < RECOVERY_WINDOW_MS)

// Mapbox's webglcontextrestored handler throws before it revives the painter, so the map never
// paints again (STX1-1068). Capture on the container beats its listener on the canvas, and only a
// remount rebuilds the painter.
const ContextLossRecovery: React.FC<{
  onRecover: (readPose: () => ICamera) => void
}> = ({ onRecover }) => {
  const { current: mapRef } = useMap()

  React.useLayoutEffect(() => {
    const map = mapRef?.getMap?.()
    if (!map) {
      return
    }
    const container = map.getContainer()
    let isActive = true

    const onRestored = (event: Event) => {
      event.stopPropagation()
      // A thunk, not a snapshot: a refused recovery keeps this map mounted for up to a
      // minute, and the app can still move its CPU-side transform in the meantime.
      // React 17 does not batch a native listener's setState, so a direct call tears the canvas
      // down inside its own dispatch. Not rAF: it is frozen while hidden, which is when this hits.
      queueMicrotask(() => isActive && onRecover(() => readCamera(map)))
    }

    container.addEventListener('webglcontextrestored', onRestored, true)

    return () => {
      isActive = false
      container.removeEventListener('webglcontextrestored', onRestored, true)
    }
  }, [mapRef, onRecover])

  return null
}

// No cursor prop by default: mapbox's own grab/grabbing rules cover it and an inline cursor
// outranks them. The gap they leave is handled by ROTATING_CLASS above, not per drag frame.
const MapCanvas = React.forwardRef<MapRef, IProps>(function MapCanvas(
  {
    mapboxAccessToken,
    cursor,
    initialViewState,
    isTrafficEnabled,
    isTerrainEnabled,
    isThreeDEnabled,
    shouldHideLabels,
    isPitchLocked,
    isInert,
    onStyleLoadedChange,
    children,
    ...props
  },
  ref,
) {
  // One state, so no generation paints without its camera.
  const [recovery, setRecovery] = React.useState<{
    generation: number
    camera?: ICamera
  }>({ generation: 0 })
  const spentRef = React.useRef<number[]>([])
  const retryRef = React.useRef<ReturnType<typeof setTimeout>>(null)

  const remount = React.useCallback((camera: ICamera) => {
    spentRef.current.push(performance.now())
    setRecovery(({ generation }) => ({ generation: generation + 1, camera }))
  }, [])

  const onRecover = React.useCallback(
    (readPose: () => ICamera) => {
      const now = performance.now()
      const recent = withinBudget(spentRef.current, now)
      spentRef.current = recent
      if (recent.length < RECOVERY_LIMIT) {
        remount(readPose())
        return
      }
      // Deferred, not dropped: the browser offers a restore once per loss, so refusing
      // outright would leave this map blank for the rest of the page's life.
      clearTimeout(retryRef.current)
      retryRef.current = setTimeout(
        () => {
          retryRef.current = null
          spentRef.current = withinBudget(spentRef.current, performance.now())
          remount(readPose())
        },
        RECOVERY_WINDOW_MS - (now - recent[0]) + 1,
      )
    },
    [remount],
  )

  React.useEffect(() => () => clearTimeout(retryRef.current), [])

  // In an effect, not at module scope: importing this file must not stamp a style on <html>, least
  // of all on remote-chunk load. Layout, because a paint without the property masks to none.
  React.useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      '--map-attribution-icon',
      MAP_ATTRIBUTION_ICON,
    )
  }, [])

  const resolvedAccessToken =
    mapboxAccessToken || mapboxgl.accessToken || Config.MAPBOX_TOKEN

  // Read through a ref, and keep the callback identity stable on purpose. `transformRequest` is
  // a mapbox CONSTRUCT-time option: react-map-gl diffs a fixed settings list on update and this
  // is not in it, so whatever function the first render supplied is the one the map keeps for
  // life. The token arrives asynchronously (Config's remote fetch), so a map created before it
  // lands captured an empty one, mapboxAccountFromToken('') returned null, and the rewrite was
  // skipped for every glyph request that map would ever make — the Inter SemiBold range then
  // comes off mapbox's shared shelf, which does not host that face, and the composite stack
  // silently falls through to Arial for the whole session. A stable callback over a live ref
  // keeps the one captured function correct once the token appears.
  const accessTokenRef = React.useRef(resolvedAccessToken)
  accessTokenRef.current = resolvedAccessToken

  const transformRequest = React.useCallback(
    (url: string, resourceType?: string) => {
      if (resourceType !== 'Glyphs') {
        return undefined
      }
      const rewritten = rewriteGlyphUrl(url, accessTokenRef.current)
      return rewritten ? { url: rewritten } : undefined
    },
    [],
  )

  return (
    <Map
      {...props}
      key={recovery.generation}
      cursor={cursor}
      ref={ref}
      // bounds dropped: react-map-gl fitBounds's over the camera when they are present,
      // which would re-frame the map instead of restoring where the user was.
      initialViewState={
        recovery.camera
          ? { ...initialViewState, ...recovery.camera, bounds: undefined }
          : initialViewState
      }
      mapboxAccessToken={resolvedAccessToken}
      transformRequest={transformRequest}
      maxZoom={MAX_ZOOM_LEVEL}
      maxPitch={isPitchLocked ? 0 : MAX_PITCH_LEVEL}
      // Mapbox's default of 7 snaps any rotation ending within 7° back to north (STX-3471).
      bearingSnap={0}
      // Off so the control below owns the credits: this option is a boolean, not the compact form.
      attributionControl={false}
      logoPosition="bottom-left"
      // Double-click places and creates on the maps that bind it, so it cannot also zoom. Off
      // everywhere, not per surface, so the gesture set stays one set.
      doubleClickZoom={false}
      // The one gesture the shield cannot reach: mapbox makes the canvas tabbable and binds
      // keydown to the container, and pointer-events does not gate keyboard input.
      keyboard={!isInert}
    >
      <AttributionControl compact />
      <MapInstanceRules isInert={isInert} />
      <ContextLossRecovery onRecover={onRecover} />
      <StyleLoadedProvider onChange={onStyleLoadedChange}>
        <MapLayers
          isTrafficEnabled={isTrafficEnabled}
          isTerrainEnabled={isTerrainEnabled}
          isThreeDEnabled={isThreeDEnabled}
          shouldHideLabels={shouldHideLabels}
        />
        {children}
      </StyleLoadedProvider>
    </Map>
  )
})

export default MapCanvas
